<template>
    <!-- Modal para subir documento -->
    <b-modal v-model="modal_file.show"
             centered
             :title="'Subir documento'"
    >
        <form @submit.prevent="validateForm()"
              data-vv-scope="file"
        >
            <b-row>
                <!-- Campo para mostrar la carpeta actual -->
                <b-col cols="12"
                       class="mb-2"
                >
                    <label for="">Carpeta</label>
                    <div class="pl-3">
                        <i class="fa fa-folder"
                           style="color: #ffa000"
                        ></i>
                        {{ file.parent.original_name }}
                    </div>
                </b-col>
                <!-- Campo para seleccionar el archivo -->
                <b-col cols="12">
                    <label for="file">Archivos</label>
                    <div class="drop-zone"
                         @dragover.prevent="onDragOver"
                         @dragenter.prevent="onDragEnter"
                         @dragleave.prevent="onDragLeave"
                         @drop.prevent="onDrop"
                         :class="{'dragging': isDragging}"
                    >
                        <div class="drop-zone-content">
                            <i class="fa fa-cloud-upload fa-2x mb-2"></i>
                            <p>Arrastra y suelta archivos aquí o</p>
                            <b-form-file id="file"
                                        name="file"
                                        :value="null"
                                        @change="handleFileChange($event.target.files)"
                                        data-vv-as=" "
                                        multiple
                                        placeholder="Elija uno o varios archivos..."
                                        ref="fileInput"
                            />
                        </div>
                    </div>
                    <!-- Mensaje de error en caso de validación fallida -->
                    <span class="text-danger small" v-if="errors.has('file.file')">
                        {{ errors.first('file.file') }}
                    </span>
                    <!-- Lista de archivos seleccionados -->
                    <div v-if="file.file && file.file.length" class="mt-2">
                        <small class="text-muted">Archivos seleccionados:</small>
                        <ul class="list-unstyled pl-3">
                            <li v-for="(fileItem, index) in file.file" :key="index" class="small d-flex align-items-center">
                                <i class="fa fa-file mr-1"></i>
                                <span class="flex-grow-1" :class="{'text-danger': fileItem.isInvalid}">{{ fileItem.name }}</span>
                                <span v-if="fileItem.isInvalid" class="text-danger ml-2">
                                    ({{ fileItem.invalidReason === 'size' ? 'Tamaño excede 5MB' : 'Tipo no permitido' }})
                                </span>
                                <b-button variant="link" 
                                          class="p-0 text-danger" 
                                          @click="removeFile(index)"
                                          size="sm">
                                    <i class="fa fa-times"></i>
                                </b-button>
                            </li>
                        </ul>
                    </div>
                </b-col>
            </b-row>
        </form>

        <template #modal-footer>
            <div class="w-100">
                <b-row>
                    <b-col cols="12"
                           class="text-center"
                    >
                        <!-- Botón para guardar el archivo -->
                        <b-button @click.prevent="validateForm"
                                  variant="primary"
                                  class="mr-1"
                                  size="sm"
                                  :disabled="!file.file || (Array.isArray(file.file) && file.file.length === 0)"
                        >
                            <i class="fa fa-save"></i> Guardar
                        </b-button>
                        <!-- Botón para cancelar -->
                        <b-button @click="setShowToModalFile(false)"
                                  variant="secondary"
                                  class="ml-1"
                                  size="sm"
                        >
                            <i class="fa fa-close"></i> Cancelar
                        </b-button>
                    </b-col>
                </b-row>
            </div>
        </template>
    </b-modal>
</template>

<script>
import Vue from 'vue';
import VeeValidate, {Validator} from 'vee-validate';
import {mapActions, mapMutations, mapState} from 'vuex';

Vue.use(VeeValidate);

// Extensión personalizada para validar tipos de archivo
Validator.extend('fileType', {
    validate: (value, args) => {
        if (!value) {
            return false;
        }

        // Si es un array de archivos
        if (Array.isArray(value)) {
            return value.every(file => {
                if (!file || !file.name) return false;
                const fileExtension = file.name.split('.').pop().toLowerCase();
                return args.includes(fileExtension);
            });
        }

        // Si es un archivo individual
        if (value && value.name) {
            const fileExtension = value.name.split('.').pop().toLowerCase();
            return args.includes(fileExtension);
        }

        return false;
    },
    getMessage: (field, args) => `El tipo de archivo no está permitido. Tipos permitidos: ${args.join(', ')}`,
});

export default {
    name: 'modal_file',
    data() {
        return {
            allowedTypes: ['docx','doc','xls','xlsx','txt','pdf','jpg','jpeg','png','bmp','zip'],
            isDragging: false
        };
    },
    computed: {
        ...mapState('calculator', [
            'modal_file',
            'file'
        ])
    },
    methods: {
        ...mapActions('calculator', [
            'getManufacturers',
            'createProduct',
            'updateProduct',
            'uploadFile'
        ]),
        ...mapMutations('calculator', [
            'setShowToModalFile'
        ]),
        
        onDragOver(event) {
            event.preventDefault();
            this.isDragging = true;
        },

        onDragEnter(event) {
            event.preventDefault();
            this.isDragging = true;
        },

        onDragLeave(event) {
            event.preventDefault();
            this.isDragging = false;
        },

        onDrop(event) {
            event.preventDefault();
            this.isDragging = false;
            
            const files = event.dataTransfer.files;
            if (files && files.length > 0) {
                this.processFiles(files);
            }
        },

        handleFileChange(files) {
            if (files && files.length > 0) {
                this.processFiles(files);
            }
        },

        processFiles(files) {
            const MAX_SIZE_MB = 5;
            const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
            
            const newFiles = Array.from(files);
            
            // Inicializar el array de archivos si no existe
            if (!this.file.file) {
                this.file.file = [];
            }
            
            // Asegurarse de que file.file sea un array
            if (!Array.isArray(this.file.file)) {
                this.file.file = [this.file.file];
            }

            const existingNames = new Set(this.file.file.map(f => f.name));
            
            const uniqueNewFiles = newFiles.filter(f => {
                const ext = f.name.split('.').pop().toLowerCase();
                
                // Validar tamaño
                if (f.size > MAX_SIZE_BYTES) {
                    f.isInvalid = true;
                    f.invalidReason = 'size';
                    return true;
                }
                
                // Validar tipo de archivo
                f.isInvalid = !this.allowedTypes.includes(ext);
                if (f.isInvalid) {
                    f.invalidReason = 'type';
                }
                
                return !existingNames.has(f.name);
            });

            // Agregar los nuevos archivos al array existente
            this.file.file = [...this.file.file, ...uniqueNewFiles];
            
            // Limpiar el input
            if (this.$refs.fileInput) {
                this.$refs.fileInput.$el.value = '';
            }
        },

        /**
         * Válida el formulario de subida de archivo.
         * Si la validación es exitosa, realiza la acción de subir el archivo.
         */
        async validateForm() {
            this.errors.clear();
            
            if (!this.file.file || (Array.isArray(this.file.file) && this.file.file.length === 0)) {
                this.errors.add({
                    field: 'file.file',
                    msg: 'Debe seleccionar al menos un archivo'
                });
                return;
            }
            // Validar tipos de archivo
            const selectedFiles = Array.isArray(this.file.file) ? this.file.file : [this.file.file];
            const invalidSelectedFiles = selectedFiles.filter(f => f.isInvalid);
            if (invalidSelectedFiles.length > 0) {
                this.errors.add({
                    field: 'file.file',
                    msg: 'Hay archivos no válidos en la lista. Elimínelos para continuar.'
                });
                return;
            }
            const company_id = this.$route.params.company_id;
            try {
                if (Array.isArray(this.file.file)) {
                    for (const singleFile of this.file.file) {
                        if (singleFile instanceof File) {
                            // Actualiza el state para que solo tenga el archivo actual
                            this.file.file = singleFile;
                            await this.uploadFile(company_id);
                        }
                    }
                } else {
                    await this.uploadFile(company_id);
                }
                this.file.file = null;
                this.setShowToModalFile(false);
                this.$emit('list');
            } catch (error) {
                this.$bvToast.toast(error.response.data.message, {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            }
        },

        /**
         * Elimina un archivo específico de la lista de selección
         * @param {number} index - Índice del archivo a eliminar
         * @author Nelson García
         */
        removeFile(index) {
            if (Array.isArray(this.file.file)) {
                this.file.file = this.file.file.filter((_, i) => i !== index);
                
                // Verificar si quedan archivos inválidos
                const invalidSelectedFiles = this.file.file.filter(f => f.isInvalid);
                if (invalidSelectedFiles.length === 0) {
                    
                    this.errors.clear();
                }
            }
        }
    }
};
</script>

<style scoped>
.drop-zone {
    border: 2px dashed #ccc;
    border-radius: 4px;
    padding: 20px;
    text-align: center;
    transition: all 0.3s ease;
    background-color: #f8f9fa;
    cursor: pointer;
}

.drop-zone.dragging {
    border-color: #007bff;
    background-color: rgba(0, 123, 255, 0.1);
}

.drop-zone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.drop-zone-content i {
    color: #6c757d;
}

.drop-zone-content p {
    margin-bottom: 1rem;
    color: #6c757d;
}
</style>
